<template>
  <menu-group class="outgoing-relations__endcard">
    <input-dropdown
      v-slot="{ item: type }"
      v-model:value="updateType"
      :options="availableUpdateTypes"
      :label="$t('labels.relationUpdateType')"
      class="mb-4"
    >
      {{ $t(`relationUpdateTypes.${type}`) }}
    </input-dropdown>

    <input-texteditor
      v-if="ctx.endElement != null"
      class="mb-2"
      :label="$t('labels.description')"
      :value="description"
      @update:value="description = $event"
    />
    <template v-if="ctx.endElement != null">
      <template v-if="updateType == 'change'">
        <h4 class="label mb-0">{{ $t("labels.effect") }}</h4>
        <div class="list mv-2">
          <input-radio
            :value="transmissionType"
            icon="not-inverse"
            data="standard"
            :label="$t('labels.relationStandard')"
            @update:value="transmissionType = $event"
          />
          <input-radio
            :value="transmissionType"
            data="inverse"
            icon="inverse"
            :label="$t('labels.relationInverse')"
            @update:value="transmissionType = $event"
          />
        </div>
      </template>

      <button class="button mt-2" type="button" @click="createRelation()">
        {{ $t("labels.createRelation") }}
      </button>
    </template>
    <template v-else>
      <p class="message mt-0 mb-2">
        {{ $t("messages.chooseElement") }}
      </p>
    </template>
  </menu-group>
</template>

<script setup lang="ts">
/**
 * End card of relation creation menu.
 * Outgoing relations are created here.
 */
import { computed } from "vue";
import { useRelationBuilder } from "./relationBuilder.js";

import MenuGroup from "@core/ui/MenuGroup.vue";
import InputRadio from "@core/input/RadioButton.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputTexteditor from "@core/input/TextEditor.vue";

import { useRelationStore } from "@editor/items/relation/relations.store";

const ctx = useRelationBuilder();

const relationStore = useRelationStore();


const transmissionType = computed({
  get() {
    return ctx.relationDraft?.transmissionType ?? "standard";
  },
  set(value: string) {
    if (ctx.relationDraftId == null) {
      console.warn("no relation draft id.");
      return;
    }
    if (!ctx.relationDraft) {
      ctx.relationDrafts[ctx.relationDraftId] = {
        transmissionType: value,
        updateType: "change",
        description: ""
      };
    } else {
      ctx.relationDraft.transmissionType = value;
    }
  }
});

const description = computed({
  get() {
    return ctx.relationDraft?.description ?? "";
  },
  set(value: string) {
    if (!ctx.relationDraft) {
      if (ctx.relationDraftId == null) {
        console.warn("no relation draft id.");
        return;
      }
      ctx.relationDrafts[ctx.relationDraftId] = {
        transmissionType: "standard",
        updateType: "change",
        description: value
      };
    } else {
      ctx.relationDraft.description = value;
    }
  }
});

const availableUpdateTypes = computed(() => {
  return ["change", "none"];
});

const updateType = computed({
  get() {
    const updateType = ctx.relationDraft?.updateType;
    if (updateType != "change" && updateType != "none") return "change";
    return updateType ?? "change";
  },
  set(value: "change" | "none") {
    if (!ctx.relationDraft) {
      if (ctx.relationDraftId == null) {
        console.warn("no relation draft id.");
        return;
      }
      ctx.relationDrafts[ctx.relationDraftId] = {
        transmissionType: "standard",
        updateType: value,
        description: ""
      };
    } else {
      ctx.relationDraft.updateType = value;
    }
  }
});

async function createRelation() {
  let inverse = transmissionType.value === "inverse";
  if (ctx.baseElement == null) {
    console.warn("can't create relation without base item.");
    return;
  }
  if (ctx.endElement == null) {
    console.warn("can't create relation without end item.");
    return;
  }

  const relation = {
    startItemId: ctx.baseElement.id,
    startItemType: ctx.baseElement.type,
    endItemId: ctx.endElement.id,
    endItemType: ctx.endElement.type,
    inverse: inverse,
    description: description.value,
    updateType: updateType.value
  };
  return relationStore.create(relation);
}
</script>

<style lang="scss">
.outgoing-relations__endcard {
  padding: 2rem 1rem;
  z-index: 1;
  position: relative;
  background-color: var(--color-background-deep);
  width: 100%;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.4);
  flex-shrink: 0;
}
</style>
